import type { ThreeElements } from '@react-three/fiber'
import { useNivelQualidade } from '../ambiente/contextoQualidade'
import { CaixaArredondada } from '../componentes/CaixaArredondada'
import { malhasFerramentasManuais } from './malhasFerramentasManuais'

interface Props extends Omit<ThreeElements['group'], 'children'> {
  ativa?: boolean
}

/** Do pé do espigão (sob a cabeça) até a ponta, inclinado para trás e para baixo. */
const BASE_ESPIGAO: [number, number, number] = [0.562, -0.018, 0]
const DIRECAO_ESPIGAO = Math.atan2(-0.13, -0.06)

/**
 * Barra Halligan, a ferramenta de arrombamento padrão dos bombeiros.
 *
 * - Cabeça forjada com cunha (adze) num contorno só, afinando até o fio em
 *   x ≈ 0,725 — o ponto de contato que a cena usa;
 * - espigão (pike) torneado, afunilando até uma ponta fina, inclinado para trás;
 * - cabo com colares cônicos e duas empunhaduras recartilhadas;
 * - garfo de dois dentes curvos chanfrados, com o encaixe em U entre eles.
 *
 * As peças forjadas têm menos metal e mais rugosidade que o cabo: faces planas
 * refletem uma direção só, e com metal quase espelhado pegavam o fundo escuro e
 * saíam pretas ao lado do cabo torneado.
 */
export function BarraHalligan3D({ ativa = false, ...props }: Props) {
  const malhas = malhasFerramentasManuais(useNivelQualidade())
  const corMetal = ativa ? '#fef08a' : '#e8edf2'
  const corGrip = ativa ? '#fde047' : '#cbd5e1'

  return (
    <group {...props}>
      {/* 1. Cabo e empunhaduras */}
      <mesh geometry={malhas.caboHalligan} castShadow>
        <meshStandardMaterial color={corGrip} metalness={0.85} roughness={0.32} />
      </mesh>

      {/* 2. Cabeça com cunha, e o espigão */}
      <mesh geometry={malhas.cabecaHalligan} castShadow>
        <meshStandardMaterial color={corMetal} metalness={0.7} roughness={0.32} />
      </mesh>
      <mesh geometry={malhas.espigao} position={BASE_ESPIGAO} rotation={[0, 0, DIRECAO_ESPIGAO]} castShadow>
        <meshStandardMaterial color={corMetal} metalness={0.7} roughness={0.32} />
      </mesh>

      {/* 3. Garfo: base e dois dentes curvos */}
      <mesh position={[-0.552, 0.002, 0]} rotation={[0, 0, -0.12]}>
        <CaixaArredondada args={[0.05, 0.036, 0.05]} />
        <meshStandardMaterial color={corMetal} metalness={0.7} roughness={0.32} />
      </mesh>
      {[1, -1].map((lado) => (
        <mesh key={lado} geometry={malhas.denteGarfo} position={[0, 0, lado * 0.017]} castShadow>
          <meshStandardMaterial color={corMetal} metalness={0.7} roughness={0.32} />
        </mesh>
      ))}
    </group>
  )
}
